import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  ResponsiveContainer, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip,
  ReferenceLine, ReferenceArea, LabelList,
} from "recharts";
import axios from "axios";
import {
  Grid, Box, Typography, Chip, Divider, Stack,
  CircularProgress, Backdrop,
  Accordion, AccordionSummary, AccordionDetails,
  Tooltip, IconButton, List, ListItem, ListItemIcon, ListItemText, ListItemSecondaryAction,
  TextField, InputAdornment,
} from "@mui/material";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import DownloadIcon from "@mui/icons-material/Download";
import { Check, Info, ArrowUpRight, Download, ChartNoAxesCombined, TrendingUp, TrendingDown, Minus, CirclePlus, Plus, Spotlight } from "lucide-react";
import { useTheme, alpha } from "@mui/material/styles";
import { useTranslation } from "../../../language";
import { API_URL } from "../../../constants";
import { useAuth } from "../../../provider";
import CustomDialogSmall from "../../../components/Dialog/DiaCompSmall";
import CustomDialog from "../../../components/Dialog/DiaCompMedium";
import ButtonForm from "../../../components/Button/ButtonForm";
import TextInfoComp from "../Bloodmarker/ReuseComps/TextInfoComp";
import MarkerTrendChart from "../Bloodmarker/ReuseComps/MarkerTrendChart";
import { SUBSYSTEM_KEY, CATEGORY_NAV_KEY } from "./bloodtestKeys";
import { DateSelector, OutOfRangePie } from "../Cockpit/HealthCockpit";

// explicit display order for Core Membership (nav_core_health) only
const SUBSYSTEM_ORDER = [
  "Sugar (Glucose) Metabolism",
  "Fat Metabolism",
  "Liver",
  "Kidney",
  "Pancreas",
  "Non-Organ-Specific",
  "Fat-Soluble-Vitamins",
  "Water-Soluble-Vitamins",
  "Minerals Micro",
  "Minerals-Macro",
  "Acute Inflammation",
  "Oxygen Transport",
  "Blood Vessel Repair",
  "White Blood Cells (Leukocytes)",
  "Granular Leukocytes",
  "Lymphocytes",
  "Monocytes",
];

// sidebar display order (top → bottom in SidebarList)
const NAV_ORDER = [
  "nav_core_health",
  "nav_arteriosclerosis_prevention",
  "nav_marker_omega_lifestyle",
  "nav_hormones_balance2",
  "nav_bones_health",
  "nav_vitamins_nutrients",
  "nav_thyroid_health",
  "nav_female_fertility_amh2",
  "nav_male_prostate_health_psa",
  "nav_stool_cancer_test_fob",
];

// Categories with their own detail page — reached via the "Einblick" chip in
// the category header once the visit has results for them
const CATEGORY_DETAIL_ROUTE = {
  "Omega Health":       { route: "/OmegaDetails" },
  "Hormones":           { route: "/BloodHormonesWelcome", femaleOnly: true },
  "Fertility (Female)": { route: "/BloodFertilityWelcome", femaleOnly: true },
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function statusColor(theme, status) {
  if (!status) return theme.palette.grey[400];
  if (status.toUpperCase() === "NORMAL") return theme.palette.traffic.green;
  return theme.palette.traffic.red;
}

function groupBySubsystem(results) {
  const grouped = (results || []).reduce((acc, r) => {
    const key = r.marker_category ?? "Other";
    if (!acc[key]) acc[key] = [];
    acc[key].push(r);
    return acc;
  }, {});
  return grouped;
}

function sortedSubsystems(grouped) {
  return Object.keys(grouped).sort((a, b) => {
    const navA = CATEGORY_NAV_KEY[a];
    const navB = CATEGORY_NAV_KEY[b];
    const posA = navA !== undefined ? NAV_ORDER.indexOf(navA) : -1;
    const posB = navB !== undefined ? NAV_ORDER.indexOf(navB) : -1;
    if (posA >= 0 && posB >= 0) return posA - posB;
    if (posA >= 0) return -1;
    if (posB >= 0) return 1;
    const numA = parseInt(a, 10), numB = parseInt(b, 10);
    if (!isNaN(numA) && !isNaN(numB)) return numA - numB;
    return String(a).localeCompare(String(b));
  });
}

function groupInternallyBySubsystem(markers) {
  const map = {};
  for (const m of markers) {
    const key = m.marker_healthsubsystem?.trim() ?? "__none__";
    if (!map[key]) map[key] = [];
    map[key].push(m);
  }
  Object.values(map).forEach((g) => g.sort((a, b) => (a.marker_order ?? 0) - (b.marker_order ?? 0)));
  return map;
}

// ─── PDF helpers (same pattern as DateController) ────────────────────────────

function getFilenameFromUrl(url) {
  try {
    const pathname = new URL(url).pathname;
    return pathname.split("/").pop() || "report.pdf";
  } catch {
    return "report.pdf";
  }
}

function formatPdfDate(isoString) {
  return new Date(isoString).toLocaleString(undefined, {
    year: "numeric", month: "short", day: "2-digit",
    hour: "2-digit", minute: "2-digit",
  });
}

function handleDownload(url) {
  const filename = getFilenameFromUrl(url);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.target = "_blank";
  a.rel = "noopener noreferrer";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

// optLow / optHigh: optional optimum range (narrower than the normal range for
// some markers); drawn as a violet band without numbers on top of the green
// normal band. Either side may be open — it then runs to the edge of the
// normal band.
function GaugeBar({ value, low, high, optLow, optHigh }) {
  const theme = useTheme();
  if (value == null || (low == null && high == null)) return null;

  const val = Number(value);
  const lo  = low  != null ? Number(low)  : null;
  const hi  = high != null ? Number(high) : null;
  const toNum = (v) => (v == null || v === "" || isNaN(parseFloat(v)) ? null : parseFloat(v));
  const oLo = toNum(optLow);
  const oHi = toNum(optHigh);
  const hasOpt = oLo != null || oHi != null;

  let dMin, dMax;
  if (lo != null && hi != null) {
    const range = (hi - lo) || 1;
    dMin = lo - range * 0.5;
    dMax = hi + range * 0.5;
  } else if (lo != null) {
    dMin = Math.min(0, lo * 0.5);
    dMax = Math.max(lo * 2, val * 1.2);
  } else {
    dMin = Math.min(0, val * 0.5);
    dMax = Math.max(hi * 1.5, val * 1.2);
  }

  const pad = (dMax - dMin) * 0.05 || 1;
  dMin = Math.min(dMin, val - pad, ...(oLo != null ? [oLo - pad] : []));
  dMax = Math.max(dMax, val + pad, ...(oHi != null ? [oHi + pad] : []));

  const span   = dMax - dMin;
  const clamp  = (v) => Math.max(0, Math.min(100, ((v - dMin) / span) * 100));
  const lowPct  = lo != null ? clamp(lo) : 0;
  const highPct = hi != null ? clamp(hi) : 100;
  const valPct  = clamp(val);
  const isNormal = (lo == null || val >= lo) && (hi == null || val <= hi);
  const optLowPct  = oLo != null ? clamp(oLo) : lowPct;
  const optHighPct = oHi != null ? clamp(oHi) : highPct;

  return (
    <Box sx={{ position: "relative", width: "100%", pb: 1 }}>
      <Box sx={{ position: "relative", width: "100%", height: 5, bgcolor: "action.disabledBackground", borderRadius: 0.5 }}>
        <Box sx={{
          position: "absolute",
          left: `${lowPct}%`,
          width: `${highPct - lowPct}%`,
          height: "100%",
          bgcolor: theme.palette.traffic.green,
          opacity: 0.4,
        }} />
        {/* Optimum range — its own colour inside the green normal band */}
        {hasOpt && optHighPct > optLowPct && (
          <Box sx={{
            position: "absolute",
            left: `${optLowPct}%`,
            width: `${optHighPct - optLowPct}%`,
            height: "100%",
            bgcolor: theme.palette.mode === "dark" ? theme.palette.quartiary.C300 : theme.palette.quartiary.C700,
          }} />
        )}
        <Box sx={{
          position: "absolute",
          top: "50%",
          left: `${valPct}%`,
          transform: "translate(-50%, -50%)",
          width: 4,
          height: 14,
          bgcolor: isNormal ? theme.palette.traffic.green : theme.palette.traffic.red,
        }} />
      </Box>
      {lo != null && (
        <Typography variant="caption" color="text.disabled" sx={{
          position: "absolute", top: "100%", left: `${lowPct}%`,
          transform: "translateX(-50%)", fontSize: "0.6rem",
          fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap",
        }}>
          {lo.toFixed(2)}
        </Typography>
      )}
      {hi != null && (
        <Typography variant="caption" color="text.disabled" sx={{
          position: "absolute", top: "100%", left: `${highPct}%`,
          transform: "translateX(-50%)", fontSize: "0.6rem",
          fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap",
        }}>
          {hi.toFixed(2)}
        </Typography>
      )}
    </Box>
  );
}

// ─── Component ───────────────────────────────────────────────────────────────

export default function BloodtestsPanel() {

  const theme = useTheme();
  const { t } = useTranslation();
  const { state } = useAuth();
  const navigate = useNavigate();
  const gender = state.user?.gender;
  const [visitOptions, setVisitOptions] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [reportError, setReportError] = useState(false);
  const [datesLoading, setDatesLoading] = useState(true);

  const [pdfReports, setPdfReports] = useState([]);
  const [pdfOpen, setPdfOpen]       = useState(false);
  // Category accordions (Core Health / each add-on) — open state toggled from a per-category default
  const [toggledCats, setToggledCats] = useState(() => new Set());

  const [cycleDay, setCycleDay]     = useState(null);
  const [cyclePhase, setCyclePhase] = useState(null);

  const [addonsOpen, setAddonsOpen] = useState(false);

  const [infoOpen, setInfoOpen]       = useState(false);
  const [infoMarker, setInfoMarker]   = useState(null);
  const [chartOpen, setChartOpen]     = useState(false);
  const [chartMarker, setChartMarker] = useState(null);

  const [userEntries, setUserEntries] = useState([]);
  const [refetchKey, setRefetchKey]   = useState(0);

  const [addValueOpen, setAddValueOpen]       = useState(false);
  const [addValueMarker, setAddValueMarker]   = useState(null);
  const [addValueDate, setAddValueDate]       = useState("");
  const [addValueVal, setAddValueVal]         = useState("");
  const [addValueRefLow, setAddValueRefLow]   = useState("");
  const [addValueRefHigh, setAddValueRefHigh] = useState("");
  const [addValueLoading, setAddValueLoading] = useState(false);
  const [addValueError, setAddValueError]     = useState("");
  const [addValueSuccess, setAddValueSuccess] = useState(false);

  /* ── 0. Fetch PDF lab reports ───────────────────────────────────────────── */

  useEffect(() => {
    axios
      .get(`${API_URL}/api/labreports/my/`)
      .then((res) => setPdfReports(res.data))
      .catch(console.error);
  }, []);

  /* ── 0c. Fetch cycle data for selected visit date (female only) ─────────── */

  useEffect(() => {
    const visitDate = visitOptions.find((v) => v.id === selectedId)?.visit_date;
    if (!visitDate || gender !== "F") return;
    axios
      .get(`${API_URL}/api/cycles/by-date/?target_date=${visitDate}`)
      .then((res) => {
        setCycleDay(res.data.cycle_day ?? null);
        setCyclePhase(res.data.cycle_phase ?? null);
      })
      .catch(() => { setCycleDay(null); setCyclePhase(null); });
  }, [selectedId, visitOptions, gender]);

  /* ── 1. Fetch all lab visits with results ───────────────────────────────── */

  useEffect(() => {
    const fetchVisits = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/all-lab-visits/results/`);
        const raw = res.data;
        const labVisits = Array.isArray(raw) ? raw : (raw.lab_visits || []);
        const entries   = Array.isArray(raw) ? [] : (raw.user_entries || []);
        const completed = labVisits.filter((v) => Number(v.state) === 6).sort((a, b) => new Date(b.visit_date) - new Date(a.visit_date));
        setVisitOptions(completed);
        setUserEntries(entries);
        if (completed.length > 0) setSelectedId((prev) => prev ?? completed[0].id);
      } catch (err) {
        console.error("Failed to fetch lab visits", err);
        setReportError("fetch");
      } finally {
        setDatesLoading(false);
      }
    };
    fetchVisits();
  }, [refetchKey]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── 2. Derive report error from selected visit data ─────────────────────── */

  useEffect(() => {
    if (!selectedId || !visitOptions.length) return;
    const v = visitOptions.find((v) => v.id === selectedId);
    setReportError(v?.results?.length ? false : "empty");
  }, [selectedId, visitOptions]);

  const report                = visitOptions.find((v) => v.id === selectedId) ?? null;
  const selectedVisit         = report;
  const currentVisitReports   = pdfReports.find((r) => r.lab_visit_id === selectedId)?.reports ?? [];
  const grouped            = report ? groupBySubsystem(report.results) : {};
  const allGroups          = report
    ? { ...Object.fromEntries(Object.keys(CATEGORY_NAV_KEY).map((k) => [k, []])), ...grouped }
    : {};
  const GENDER_EXCLUDE = {
    F: ["Prostate Cancer Prevention"],
    M: ["Fertility (Female)"],
  };
  const orderedSubsystems  = sortedSubsystems(allGroups)
    .filter((sub) => !(GENDER_EXCLUDE[gender] ?? []).includes(sub));

  /* ── Add historical value ───────────────────────────────────────────────── */

  async function handleAddValueSubmit() {
    if (!addValueMarker || !addValueDate || !addValueVal) return;
    setAddValueLoading(true);
    setAddValueError("");
    try {
      await axios.post(`${API_URL}/api/user-marker-entries/`, {
        marker:    addValueMarker.marker_id,
        date:      addValueDate,
        value:     addValueVal,
        unit:      addValueMarker.unit,
        ...(addValueRefLow  ? { ref_low:  addValueRefLow  } : {}),
        ...(addValueRefHigh ? { ref_high: addValueRefHigh } : {}),
      });
      setAddValueSuccess(true);
      setRefetchKey((k) => k + 1);
      setTimeout(() => { setAddValueOpen(false); setAddValueSuccess(false); }, 1500);
    } catch (err) {
      const data = err.response?.data ?? {};
      if (data.non_field_errors?.[0]) {
        setAddValueError(t("bt_add_value_duplicate"));
      } else {
        const msg = Object.entries(data)
          .map(([field, errs]) => `${field}: ${Array.isArray(errs) ? errs[0] : errs}`)
          .join(" · ");
        setAddValueError(msg || t("bt_add_value_error"));
      }
    } finally {
      setAddValueLoading(false);
    }
  }

  /* ── Loading ─────────────────────────────────────────────────────────────── */

  if (datesLoading) {
    return (
      <Backdrop open sx={{ zIndex: (t) => t.zIndex.drawer + 1 }}>
        <CircularProgress />
      </Backdrop>
    );
  }

  if (visitOptions.length === 0) {
    return (
      <Box sx={{ textAlign: "center", mt: 6 }}>
        <Typography color="text.secondary">{t("bt_no_visits")}</Typography>
      </Box>
    );
  }

  /* ── Render ──────────────────────────────────────────────────────────────── */

  const metaLabelSx = {
    fontSize: "0.85rem", fontWeight: 600, lineHeight: 1.3,
    color: theme.palette.mode === "dark" ? "grey.C400" : "greydark.C500",
  };
  const detailValueSx = {
    fontSize: "0.98rem", fontWeight: 600, lineHeight: 1.35, overflowWrap: "anywhere",
    color: theme.palette.mode === "dark" ? "grey.C50" : "greydark.C900",
  };
  // Plain arrow — opens a dialog without a heavy button surface
  const plainArrowSx = {
    color: theme.palette.mode === "dark" ? "grey.C50" : "greydark.C900",
    transition: "background-color 150ms ease, transform 160ms cubic-bezier(0.23, 1, 0.32, 1)",
    "& .bt-arrow": { transition: "transform 200ms cubic-bezier(0.23, 1, 0.32, 1)" },
    "@media (hover: hover) and (pointer: fine)": { "&:hover .bt-arrow": { transform: "translate(2px, -2px)" } },
    "&:active": { transform: "scale(0.94)" },
  };
  // Dark round icon button (same surface as the date button)
  const roundBtnSx = {
    width: 36, height: 36, flexShrink: 0,
    bgcolor: theme.palette.greydark.C900, color: "#fff",
    border: theme.palette.mode === "dark" ? "1px solid rgba(255,255,255,0.14)" : "none",
    transition: "filter 150ms ease, transform 160ms cubic-bezier(0.23, 1, 0.32, 1)",
    "& .bt-arrow-down": { transition: "transform 200ms cubic-bezier(0.23, 1, 0.32, 1)" },
    "@media (hover: hover) and (pointer: fine)": {
      "&:hover": { bgcolor: theme.palette.greydark.C900, filter: "brightness(1.2)" },
      "&:hover .bt-arrow-down": { transform: "translateY(2px)" },
    },
    "&:active": { transform: "scale(0.94)" },
    "&.Mui-disabled": { bgcolor: alpha(theme.palette.greydark.C900, 0.25), color: "#fff" },
  };

  return (
    <Grid container justifyContent="center" sx={{ mb: 10 }}>
      {/* ── Visit info card ───────────────────────────────────────────────── */}
      <Grid size={12} sx={{ display: "flex", justifyContent: "center", px: 2, mb: 3 }}>
        <Box sx={{ width: "100%", maxWidth: 800 }}>
          {report && !reportError && (<>
          <Box sx={{
            p: { xs: 2.25, sm: 3 }, borderRadius: "24px",
            bgcolor: theme.palette.mode === "dark" ? alpha("#fff", 0.04) : alpha("#fff", 0.5),
            backdropFilter: "blur(28px) saturate(180%)",
            WebkitBackdropFilter: "blur(28px) saturate(180%)",
            border: `1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.08)" : "rgba(255,255,255,0.65)"}`,
            boxShadow: theme.palette.mode === "dark"
              ? "inset 0 1px 0 rgba(255,255,255,0.06), 0 8px 32px rgba(0,0,0,0.55)"
              : "inset 0 1px 0 rgba(255,255,255,0.7), 0 4px 24px rgba(0,0,0,0.06)",
          }}>
            {/* Left: title + visit date dropdown | right: the visit's details, top-aligned with the title */}
            <Box sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "minmax(0, 1fr) minmax(0, 1.15fr)" },
              columnGap: 4, rowGap: 2.5, alignItems: "start",
            }}>
              <Box sx={{ minWidth: 0 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Typography variant="h4" fontWeight={700} sx={{ color: theme.palette.mode === "dark" ? "grey.C50" : "greydark.C900", letterSpacing: -0.05 }}>
                  {t("bt_labvisit")}
                </Typography>
                <Chip
                  label={selectedVisit?.visit_number ?? "—"}
                  size="small"
                  sx={{
                    bgcolor: theme.palette.mode === "dark" ? theme.palette.grey.C50    : theme.palette.greydark.C900,
                    color:   theme.palette.mode === "dark" ? theme.palette.greydark.C900 : theme.palette.grey.C50,
                    fontWeight: 600,
                    fontSize: "0.8rem",
                  }}
                />
              </Box>
                <Box sx={{ mt: 2 }}>
                  <DateSelector
                    dates={visitOptions.map((v) => v.visit_date)}
                    selectedDate={selectedVisit?.visit_date}
                    onSelect={(date) => setSelectedId(visitOptions.find((v) => v.visit_date === date)?.id ?? selectedId)}
                  />
                </Box>
              </Box>

              <Box component="dl" sx={{ m: 0, display: "flex", flexDirection: "column" }}>
                {[
                  {
                    key: "lab",
                    label: t("bt_lab"),
                    value: <Typography sx={detailValueSx}>{report.laboratory_name || "—"}</Typography>,
                  },
                  {
                    key: "addons",
                    label: t("bt_tab_addons"),
                    value: (
                      <>
                        <Typography sx={{ ...detailValueSx, fontVariantNumeric: "tabular-nums" }}>{selectedVisit?.addon_names?.length ?? 0}</Typography>
                        <Tooltip title={t("bt_tab_addons")} placement="top">
                          <IconButton onClick={() => setAddonsOpen(true)} aria-label={t("bt_tab_addons")} sx={plainArrowSx}>
                            <Box className="bt-arrow" sx={{ display: "flex" }}><ArrowUpRight size={20} strokeWidth={2.75} /></Box>
                          </IconButton>
                        </Tooltip>
                      </>
                    ),
                  },
                  {
                    key: "pdf",
                    label: t("bt_raw_report_download"),
                    value: (
                      <Tooltip title={t("bt_raw_report_download")} placement="top">
                        <span>
                          <IconButton
                            disabled={currentVisitReports.length === 0}
                            // One PDF downloads straight away; several open the list
                            onClick={() => currentVisitReports.length === 1
                              ? handleDownload(currentVisitReports[0].file_url)
                              : setPdfOpen(true)}
                            aria-label={t("bt_raw_report_download")}
                            sx={roundBtnSx}
                          >
                            <Box className="bt-arrow-down" sx={{ display: "flex" }}><Download size={17} strokeWidth={2.5} /></Box>
                          </IconButton>
                        </span>
                      </Tooltip>
                    ),
                  },
                  ...(gender === "F" ? [{
                    key: "cycle",
                    label: t("bt_cycle_status"),
                    value: (
                      <Typography sx={{ ...detailValueSx, textTransform: "capitalize" }}>
                        {cyclePhase ?? "—"}
                        {cycleDay != null && (
                          <Box component="span" sx={{ color: "text.secondary", fontWeight: 400, textTransform: "none" }}>
                            {" · "}{t("bt_cycle_day").replace("{n}", cycleDay)}
                          </Box>
                        )}
                      </Typography>
                    ),
                  }] : []),
                ].map((row, i) => (
                  <Box key={row.key}>
                    {i > 0 && <Divider sx={{ borderColor: alpha(theme.palette.mode === "dark" ? "#fff" : theme.palette.greydark.C900, 0.08) }} />}
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, minHeight: 52, py: 0.75, ...(i === 0 && { pt: 0, minHeight: 44 }) }}>
                      <Typography component="dt" sx={metaLabelSx}>{row.label.replace(/:\s*$/, "")}:</Typography>
                      <Box component="dd" sx={{ m: 0, display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 1, minWidth: 0, textAlign: "right" }}>
                        {row.value}
                      </Box>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>

          {/* Ordered add-on packages of the selected visit */}
          <CustomDialogSmall
            open={addonsOpen}
            onClose={() => setAddonsOpen(false)}
            title={t("bt_tab_addons")}
            content={selectedVisit?.addon_names?.length > 0 ? (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                {selectedVisit.addon_names.map((name) => (
                  <Chip key={name} label={name} variant="outlined" sx={{ fontSize: "0.85rem", fontWeight: 500 }} />
                ))}
              </Box>
            ) : (
              <Typography variant="body1" color="text.secondary">{t("bt_no_addons")}</Typography>
            )}
          />

          </>)}

       <Divider sx={{ mt: 4, mb: 4 }} />

      {/* ── Empty / error state ───────────────────────────────────────────── */}
      {reportError && (
        <Grid size={12} sx={{ display: "flex", justifyContent: "center", px: 2 }}>
          <Box sx={{
            width: "100%", maxWidth: 700,
            p: 2.5, borderRadius: 3,
            border: "0.5px solid", borderColor: "divider",
            textAlign: "center",
          }}>
            <Typography color="text.secondary">
              {reportError === "empty" ? t("bt_no_results_yet") : t("bt_results_error")}
            </Typography>
          </Box>
        </Grid>
      )}

      {/* ── Report content ────────────────────────────────────────────────── */}
      {report && !reportError && (<>

        <Grid size={12} sx={{ display: "flex", justifyContent: "center", px: 0.5, mb: 4 }}>
          {/* Heading | out-of-range ring (same as on the Health Cockpit) */}
          <Box sx={{ width: "100%", maxWidth: 800, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h4" fontWeight={700} sx={{ letterSpacing: -0.5 }}>
                {t("bt_results_header")}
              </Typography>
              {/* Key for the range gauges in the marker rows */}
              <Box sx={{ mt: 1, display: "flex", flexWrap: "wrap", columnGap: 2, rowGap: 0.5 }}>
                {[
                  { label: t("bt_legend_optimal"), color: theme.palette.mode === "dark" ? theme.palette.quartiary.C300 : theme.palette.quartiary.C700, opacity: 1 },
                  { label: t("bt_legend_normal"), color: theme.palette.traffic.green, opacity: 0.4 },
                ].map(({ label, color, opacity }) => (
                  <Box key={label} sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }}>
                    <Box sx={{ width: 18, height: 5, borderRadius: 0.5, bgcolor: color, opacity }} />
                    <Typography sx={{ fontSize: "0.8rem", color: "text.secondary", lineHeight: 1.4 }}>{label}</Typography>
                  </Box>
                ))}
              </Box>
            </Box>
            <Box sx={{ width: { xs: 130, sm: 160 }, flexShrink: 0 }}>
              <OutOfRangePie visit={selectedVisit} visits={visitOptions} height={{ xs: 130, sm: 160 }} />
            </Box>
          </Box>
        </Grid>

        {/* Results by healthsubsystem */}
        {orderedSubsystems.map((sub, subIdx) => {
          const markers     = allGroups[sub] ?? [];
          const tKey        = CATEGORY_NAV_KEY[sub];
          const catLabel    = tKey ? t(tKey) : String(sub);
          const redCount    = markers.filter((m) => m.status && m.status.toUpperCase() !== "NORMAL").length;
          const isCoreHealth = tKey === "nav_core_health";
          const prevTKey     = subIdx > 0 ? CATEGORY_NAV_KEY[orderedSubsystems[subIdx - 1]] : null;
          const isFirstAddon = prevTKey === "nav_core_health";

          return (
            <Grid size={12} key={sub} sx={{ display: "flex", justifyContent: "center", px: 0.5, mb: 2, ...(isFirstAddon && { mt: 4 }) }}>
              <Box sx={{ width: "100%", maxWidth: 800 }}>
                {isCoreHealth && (
                  <Typography variant="h5" fontWeight={700} sx={{ letterSpacing: -0.3, mb: 1.5 }}>
                    {t("bt_core_result_header")} {selectedVisit?.visit_number ?? ""}
                  </Typography>
                )}
                {isFirstAddon && (
                  <Typography variant="h5" fontWeight={700} sx={{ letterSpacing: -0.3, mb: 1.5 }}>
                    {t("bt_addon_result_header")}
                  </Typography>
                )}
                {/* Category — its own glass card, collapsible; all closed by default */}
                {(() => {
                  const catOpen = toggledCats.has(sub);
                  const toggleCat = () => {
                    setToggledCats((prev) => {
                      const next = new Set(prev);
                      if (next.has(sub)) next.delete(sub); else next.add(sub);
                      return next;
                    });
                  };
                  const dark = theme.palette.mode === "dark";
                  const glassSx = {
                    borderRadius: "16px",
                    overflow: "hidden",
                    bgcolor: dark ? alpha("#fff", 0.04) : alpha("#fff", 0.5),
                    backdropFilter: "blur(28px) saturate(180%)",
                    WebkitBackdropFilter: "blur(28px) saturate(180%)",
                    border: `1px solid ${dark ? "rgba(255,255,255,0.05)" : "rgba(255,255,255,0.35)"}`,
                    boxShadow: dark
                      ? "inset 0 1px 0 rgba(255,255,255,0.06), 0 8px 32px rgba(0,0,0,0.55)"
                      : "inset 0 1px 0 rgba(255,255,255,0.7), 0 4px 24px rgba(0,0,0,0.06)",
                  };
                  const subBoxSx = {
                    borderRadius: "12px",
                    overflow: "hidden",
                    bgcolor: dark ? alpha("#fff", 0.03) : alpha("#000", 0.02),
                    border: `1px solid ${dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)"}`,
                  };

                  return (
                    <Box sx={glassSx}>
                      <Accordion
                        expanded={catOpen}
                        onChange={toggleCat}
                        disableGutters
                        elevation={0}
                        square
                        slotProps={{ transition: { timeout: 320 } }}
                        sx={{ bgcolor: "transparent", boxShadow: "none", "&:before": { display: "none" } }}
                      >
                        <AccordionSummary
                          expandIcon={catOpen
                            ? <Minus size={18} color={theme.palette.mode === "light" ? theme.palette.greydark.C900 : theme.palette.grey.C50} />
                            : <Plus size={18} color={theme.palette.mode === "light" ? theme.palette.greydark.C900 : theme.palette.grey.C50} />}
                          sx={{ px: 2.5, minHeight: 56, "& .MuiAccordionSummary-content": { my: 1, alignItems: "center", gap: 1.25 }, "& .MuiAccordionSummary-expandIconWrapper": { ml: 1.5 } }}
                          {...(subIdx === 0 ? { 'data-tour': 'laborwerte-marker' } : {})}
                        >
                          <Typography variant="subtitle1" fontWeight={700} sx={{ letterSpacing: 0.2 }}>
                            {catLabel}
                          </Typography>
                          <Box sx={{ flexGrow: 1 }} />
                          {(() => {
                            const detail = CATEGORY_DETAIL_ROUTE[sub];
                            if (!detail || markers.length === 0 || (detail.femaleOnly && gender !== "F")) return null;
                            return (
                              <Tooltip title={t("bt_tooltip_view_detail")} placement="top">
                                <Chip
                                  icon={<Spotlight size={14} color="#fff" />}
                                  label={t("bt_omega_insight_label")}
                                  size="small"
                                  onClick={(e) => { e.stopPropagation(); navigate(detail.route); }}
                                  sx={{
                                    mr: 1.5,
                                    bgcolor: "quartiary.C700",
                                    color: "#fff",
                                    fontWeight: 700, fontSize: "0.65rem",
                                    borderRadius: "6px",
                                    "& .MuiChip-icon": { ml: 0.5 },
                                    cursor: "pointer",
                                    "&:hover": { transform: "scale(1.05)" },
                                  }}
                                />
                              </Tooltip>
                            );
                          })()}
                          {redCount > 0 ? (
                            <Tooltip title={t("bt_tooltip_out_of_range")} placement="top">
                              <Chip
                                label={`${redCount}`}
                                size="small"
                                sx={{
                                  bgcolor: "transparent",
                                  color: theme.palette.traffic.red,
                                  border: "1px solid", borderColor: theme.palette.traffic.red,
                                  fontWeight: 500, fontSize: "0.8rem",
                                  width: 22, height: 22,
                                  borderRadius: "999px",
                                  "& .MuiChip-label": { px: 0, display: "flex", alignItems: "center", justifyContent: "center", width: "100%" },
                                }}
                              />
                            </Tooltip>
                          ) : markers.length > 0 && (
                            <Tooltip title={t("bt_tooltip_all_in_range")} placement="top">
                              <Box sx={{ display: "flex", alignItems: "center" }}>
                                <Check size={18} strokeWidth={2.5} color={theme.palette.traffic.green} />
                              </Box>
                            </Tooltip>
                          )}
                        </AccordionSummary>
                        <AccordionDetails sx={{ p: 0 }}>
                          {markers.length === 0 ? (
                            <Box sx={{ px: 2.5, py: 2 }}>
                              <Typography variant="body2" fontWeight={700} color="text.disabled" sx={{ textTransform: "uppercase", letterSpacing: 0.4 }}>
                                {t("bt_not_ordered")}
                              </Typography>
                              <Typography variant="body2" color="text.disabled" sx={{ mt: 0.5 }}>
                                {t("bt_consider_addon")}
                              </Typography>
                            </Box>
                          ) : (() => {
                              const internalMap = groupInternallyBySubsystem(markers);
                              const subKeys = Object.keys(internalMap).sort((a, b) => {
                                if (a === "__none__") return 1;
                                if (b === "__none__") return -1;
                                if (isCoreHealth) {
                                  const posA = SUBSYSTEM_ORDER.indexOf(a);
                                  const posB = SUBSYSTEM_ORDER.indexOf(b);
                                  if (posA >= 0 && posB >= 0) return posA - posB;
                                  if (posA >= 0) return -1;
                                  if (posB >= 0) return 1;
                                }
                                return a.localeCompare(b);
                              });

                              return (
                                <Box sx={{ px: 1.5, pb: 1.5, display: "flex", flexDirection: "column", gap: 1.25 }}>
                                  {subKeys.map((sk) => {
                                    const subMarkers  = internalMap[sk];
                                    const subLabelKey = SUBSYSTEM_KEY[sk];
                                    const subLabel    = sk === "__none__" ? null : (subLabelKey ? t(subLabelKey) : sk);

                                    const markerRows = subMarkers.map((m, mi) => {
                                      const statusKey  = `bt_status_${(m.status || "").toLowerCase().replace(/\s+/g, "_")}`;
                                      const statusText = t(statusKey) !== statusKey ? t(statusKey) : m.status;
                                      return (
                                        <Box key={m.id}>
                                          {mi > 0 && <Divider sx={{ mx: 2 }} />}
                                          <Box sx={{ display: "flex", alignItems: "center", px: 2, py: 0.75, columnGap: 2, rowGap: { xs: 1, sm: 2 }, flexWrap: { xs: "wrap", sm: "nowrap" } }}>
                                            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, flexGrow: 1, minWidth: 120 }}>
                                              <Box sx={{ width: 8, height: 8, borderRadius: "50%", flexShrink: 0, bgcolor: statusColor(theme, m.status), mt: "7px" }} />
                                              <Box sx={{ minWidth: 0 }}>
                                                <Typography variant="body1" fontWeight={500} sx={{ fontSize: { xl: "1rem" } }}>{t(`markername_${m.marker_short}`)}</Typography>
                                                <Typography variant="subtitle1" color="text.secondary" sx={{ fontSize: { xl: "0.9rem" } }}>{t(`markerdescr_${m.marker_short}`)}</Typography>
                                              </Box>
                                            </Box>
                                            {/* Mobile-only line break: forces the value+gauge pair onto their own row */}
                                            <Box sx={{ flexBasis: "100%", height: 0, display: { xs: "block", sm: "none" } }} />
                                            {/* Grouped into one flex item so a long unit label (e.g. "mOsm/kg") can't
                                                push the gauge onto its own separate line — the pair wraps as a unit. */}
                                            <Box sx={{ display: "flex", alignItems: "center", flexWrap: "nowrap", ml: { xs: "26px", sm: 0 } }}>
                                              <Typography variant="number3" fontWeight={400} sx={{ minWidth: { xs: "auto", sm: 80 }, textAlign: { xs: "left", sm: "right" }, fontVariantNumeric: "tabular-nums", flexShrink: 0 }}>
                                                {m.value != null ? Number(m.value).toFixed(2) : "—"}{m.unit ? ` ${m.unit}` : ""}
                                              </Typography>
                                              <Box sx={{ minWidth: 140, ml: 2, display: "flex", alignItems: "center" }}>
                                                <GaugeBar value={m.value} low={m.ref_low} high={m.ref_high} optLow={m.opt_low} optHigh={m.opt_high} />
                                              </Box>
                                            </Box>
                                            {/* Mobile-only line break: forces the check/info/chart/plus icons onto their own row */}
                                            <Box sx={{ flexBasis: "100%", height: 0, display: { xs: "block", sm: "none" } }} />
                                            <Box sx={{ minWidth: { xs: "auto", sm: 68 }, ml: { xs: "26px", sm: 0 }, display: "flex", justifyContent: { xs: "flex-start", sm: "flex-end" }, flexShrink: 0 }}>
                                              {m.status?.toUpperCase() === "NORMAL"
                                                ? <Check size={18} strokeWidth={3} color={theme.palette.traffic.green} />
                                                : <Typography variant="subtitle2" fontWeight={700} sx={{ color: statusColor(theme, m.status) }}>{statusText}</Typography>
                                              }
                                            </Box>
                                            <Tooltip title="Info" placement="top">
                                              <IconButton size="small" onClick={() => { setInfoMarker(m.marker_short); setInfoOpen(true); }} sx={{ '&:hover': { transform: 'scale(1.2)' } }}>
                                                <Info size={18} color={theme.palette.secondary.main} />
                                              </IconButton>
                                            </Tooltip>
                                            <Tooltip title={t("bt_chart_history")} placement="top">
                                              <IconButton size="small" onClick={() => { setChartMarker(m.marker_short); setChartOpen(true); }} sx={{ '&:hover': { transform: 'scale(1.2)' } }}>
                                                <ChartNoAxesCombined size={18} color={theme.palette.primary.main} />
                                              </IconButton>
                                            </Tooltip>
                                            <Tooltip title={t("bt_add_value_title")} placement="top">
                                              <IconButton size="small" onClick={() => {
                                                setAddValueMarker({ marker_id: m.marker, marker_short: m.marker_short, marker_name: t(`markername_${m.marker_short}`), unit: m.unit ?? "", ref_low: m.ref_low ?? "", ref_high: m.ref_high ?? "" });
                                                setAddValueDate("");
                                                setAddValueVal("");
                                                setAddValueRefLow(m.ref_low ?? "");
                                                setAddValueRefHigh(m.ref_high ?? "");
                                                setAddValueError("");
                                                setAddValueOpen(true);
                                              }} sx={{ '&:hover': { transform: 'scale(1.2)' } }}>
                                                <CirclePlus size={18} color={theme.palette.quartiary.C300} />
                                              </IconButton>
                                            </Tooltip>
                                          </Box>
                                        </Box>
                                      );
                                    });

                                    return (
                                      <Box key={sk} sx={subBoxSx}>
                                        {subLabel && (
                                          <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 2, pt: 1.5, pb: 1 }}>
                                            <Box sx={{ width: 7, height: 7, borderRadius: "50%", flexShrink: 0, bgcolor: "tertiary.C300" }} />
                                            <Typography variant="caption" fontWeight={700} color="text.secondary"
                                              sx={{ textTransform: "uppercase", letterSpacing: 0.6 }}>
                                              {subLabel}
                                            </Typography>
                                          </Box>
                                        )}
                                        {markerRows}
                                      </Box>
                                    );
                                  })}
                                </Box>
                              );
                          })()}
                        </AccordionDetails>
                      </Accordion>
                    </Box>
                  );
                })()}
              </Box>
            </Grid>
          );
        })}

      </>)}

      <CustomDialog
        open={chartOpen}
        onClose={() => setChartOpen(false)}
        title={chartMarker ? `${t(`markername_${chartMarker}`)} — ${t("bt_chart_title")}` : ""}
        content={
          chartMarker && (
            <MarkerTrendChart
              markerShort={chartMarker}
              visitOptions={visitOptions}
              userEntries={userEntries}
              theme={theme}
              t={t}
            />
          )
        }
      />

      <CustomDialogSmall
        open={pdfOpen}
        onClose={() => setPdfOpen(false)}
        title="PDF Report Download"
        content={
          currentVisitReports.length === 0 ? (
            <Typography sx={{ p: 3 }} color="text.secondary">No reports available.</Typography>
          ) : (
            <Box>
              <Divider />
              <List disablePadding>
                {currentVisitReports.map((report, idx) => (
                  <Box key={report.id}>
                    <ListItem sx={{ pr: 8, py: 2, px: 2.5 }}>
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        <PictureAsPdfIcon sx={{ fontSize: "1.8rem", color: "text.secondary" }} />
                      </ListItemIcon>
                      <ListItemText
                        primary={currentVisitReports.length > 1 ? `Report ${idx + 1}` : "Report"}
                        secondary={formatPdfDate(report.uploaded_at)}
                        primaryTypographyProps={{ variant: "h6", fontWeight: 700 }}
                        secondaryTypographyProps={{ variant: "caption" }}
                      />
                      <ListItemSecondaryAction>
                        <Tooltip title="Download PDF">
                          <IconButton
                            edge="end"
                            onClick={() => handleDownload(report.file_url)}
                            color="secondary"
                            aria-label={`download ${report.description}`}
                          >
                            <DownloadIcon />
                          </IconButton>
                        </Tooltip>
                      </ListItemSecondaryAction>
                    </ListItem>
                    <Divider />
                  </Box>
                ))}
              </List>
            </Box>
          )
        }
      />

  </Box>
      </Grid>

      <CustomDialogSmall
        open={infoOpen}
        onClose={() => { setInfoOpen(false); setInfoMarker(null); }}
        title="Information"
        content={infoMarker && (
          <TextInfoComp
            description={`description_${infoMarker}`}
            lowtext={`lowtext_${infoMarker}`}
            lowrec={`lowrec_${infoMarker}`}
            rangetext={`rangetext_${infoMarker}`}
            hightext={`hightext_${infoMarker}`}
            highrec={`highrec_${infoMarker}`}
          />
        )}
      />

      <CustomDialogSmall
        open={addValueOpen}
        onClose={() => { setAddValueOpen(false); setAddValueError(""); }}
        title={t("bt_add_value_title")}
        content={
          addValueMarker && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2, px: 1, pt: 1 }}>
              <Typography variant="body1" color="text.secondary" sx={{ textAlign: "center", mt: -1 }}>
                {t("bt_add_value_subtitle")}
              </Typography>
              <Typography variant="body1" color="text.disabled" sx={{ textAlign: "center", mt: -1.5 }}>
                {t("bt_add_value_subtitle2")}
              </Typography>
              {addValueSuccess && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, p: 1.5, borderRadius: 2, bgcolor: theme.palette.traffic.green + "22", border: "1px solid", borderColor: theme.palette.traffic.green }}>
                  <Check size={18} color={theme.palette.traffic.green} strokeWidth={2.5} />
                  <Typography variant="body2" fontWeight={600} sx={{ color: theme.palette.traffic.green }}>{t("bt_add_value_success")}</Typography>
                </Box>
              )}
              <Box sx={{ display: "flex", gap: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="caption" color="text.disabled" sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}>
                    {t("bt_add_value_marker")}
                  </Typography>
                  <Typography variant="subtitle2" fontWeight={700}>{addValueMarker.marker_name}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.disabled" sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}>
                    {t("bt_add_value_unit")}
                  </Typography>
                  <Typography variant="subtitle2" fontWeight={700}>{addValueMarker.unit || "—"}</Typography>
                </Box>
              </Box>

              <TextField
                label={t("bt_add_value_date")}
                type="date"
                value={addValueDate}
                onChange={(e) => setAddValueDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
                size="small"
              />

              <TextField
                label={t("bt_add_value_value")}
                value={addValueVal}
                onChange={(e) => setAddValueVal(e.target.value)}
                type="number"
                fullWidth
                size="small"
                InputProps={addValueMarker.unit ? {
                  endAdornment: <InputAdornment position="end">{addValueMarker.unit}</InputAdornment>,
                } : undefined}
              />

              <Box sx={{ display: "flex", gap: 1.5 }}>
                <TextField
                  label={t("bt_add_value_ref_low")}
                  value={addValueRefLow}
                  onChange={(e) => setAddValueRefLow(e.target.value)}
                  type="number"
                  fullWidth
                  size="small"
                />
                <TextField
                  label={t("bt_add_value_ref_high")}
                  value={addValueRefHigh}
                  onChange={(e) => setAddValueRefHigh(e.target.value)}
                  type="number"
                  fullWidth
                  size="small"
                />
              </Box>

              {addValueError && (
                <Typography variant="body2" color="error">{addValueError}</Typography>
              )}
            </Box>
          )
        }
        action={
          <ButtonForm
            name={t("bt_add_value_submit")}
            disabled={addValueLoading || !addValueDate || !addValueVal}
            onClick={handleAddValueSubmit}
            type="button"
          />
        }
      />

    </Grid>
  );
}
